import { useQuery } from '@tanstack/react-query'
import axios from '@pepper/utils/src/function/axiosInstance'
import { SEO_SERVICE_BASE_URL } from '@pepper/utils/src/api/config'

import { Keyword } from 'types/keyword'

type QueryParamsFilter = {
  keyword?: string[]
  gteDifficulty?: number
  lteDifficulty?: number
  lteVolume?: number
  gteVolume?: number
  intent?: 0 | 1 | 2 | 3
  sort?: string
  exists?: boolean
  positionLte?: number
  positionGte?: number
  exclude_position?: boolean
}

type Props = {
  projectId: string
  companyId: string
  queryParamsFilter?: QueryParamsFilter
  page?: number
  limit?: number
  enabled?: boolean
}

const getComparisonValues = (
  difficulty?: number,
  volume?: number,
  position?: number
) => {
  const comparisonValue = []
  if (difficulty) comparisonValue.push(`difficulty:${difficulty}`)
  if (volume) comparisonValue.push(`volume:${volume}`)
  if (position) comparisonValue.push(`self_top_position:${position}`)
  return comparisonValue
}

const getProjectKeywords = async ({
  projectId,
  companyId,
  queryParamsFilter,
  page,
  limit,
}: Props) => {
  const url = `${SEO_SERVICE_BASE_URL}/companies/${companyId}/keywords/v2`

  const keyword = queryParamsFilter?.keyword
  const intent = queryParamsFilter?.intent
  const lte = getComparisonValues(
    queryParamsFilter?.lteDifficulty,
    queryParamsFilter?.lteVolume,
    queryParamsFilter?.positionLte
  )
  const gte = getComparisonValues(
    queryParamsFilter?.gteDifficulty,
    queryParamsFilter?.gteVolume,
    queryParamsFilter?.positionGte
  )

  const response = await axios.get(url, {
    params: {
      project_id: projectId,
      page,
      limit,
      keyword,
      intent,
      lte,
      gte,
      sort: queryParamsFilter?.sort,
      exists: queryParamsFilter?.exists ? `self_top_position:false` : undefined,
    },
    paramsSerializer: {
      indexes: null,
    },
  })
  return response?.data
}

export const useProjectKeywords = ({
  projectId,
  companyId,
  queryParamsFilter,
  page,
  limit,
  enabled = true,
}: Props) => {
  return useQuery<
    { records: Keyword[]; total_records: number },
    {
      response: {
        status: number
      }
    }
  >({
    queryKey: [
      'project-keywords',
      projectId,
      companyId,
      queryParamsFilter,
      page,
      limit,
    ],
    queryFn: () =>
      getProjectKeywords({
        projectId,
        companyId,
        queryParamsFilter,
        page,
        limit,
      }),
    retry: false,
    enabled,
  })
}
